// Feature 009: Deal Operating System
// Status column for Kanban pipeline view
// Design: Black (#000), Bone (#fffbf7), Sage (#a1c6a2)
import React, { useMemo } from 'react';
import { useDroppable } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import OpportunityCard from './OpportunityCard';

const StatusColumn = ({ status, opportunities }) => {
  const { setNodeRef, isOver, active } = useDroppable({
    id: status.key,
  });

  const sortedOpportunities = useMemo(() => {
    return [...opportunities].sort((a, b) => {
      // Sort by last activity, stale items at top
      if (a.stale && !b.stale) return -1;
      if (!a.stale && b.stale) return 1;
      return new Date(b.last_activity_at || b.updated_at) - new Date(a.last_activity_at || a.updated_at);
    });
  }, [opportunities]);

  // Determine if we're the valid drop target (different from source)
  const isDragging = !!active;
  const isValidDropTarget = isOver && active?.id;

  return (
    <div
      ref={setNodeRef}
      className={`flex flex-col w-72 border bg-[#0a0a0a] transition-all duration-200 ${
        isValidDropTarget
          ? 'border-[#a1c6a2] bg-[rgba(161,198,162,0.05)] scale-[1.01]'
          : isDragging
            ? 'border-[rgba(255,251,247,0.2)]'
            : 'border-[rgba(255,251,247,0.1)]'
      }`}
    >
      {/* Column Header */}
      <div className="px-4 py-3 border-b border-[rgba(255,251,247,0.1)] bg-black flex justify-between items-center">
        <div className="flex items-center gap-3">
          <span
            className="text-[10px] text-[#a1c6a2] tracking-wider"
            style={{ fontFamily: "'GT Pressura Mono', monospace" }}
          >
            {status.code}
          </span>
          <span
            className="text-xs font-semibold text-[#fffbf7] tracking-[0.1em] uppercase"
            style={{ fontFamily: "'GT Pressura Mono', monospace" }}
          >
            {status.label}
          </span>
        </div>
        <span
          className="bg-[rgba(255,251,247,0.1)] text-[rgba(255,251,247,0.6)] text-[10px] font-medium px-2 py-0.5"
          style={{ fontFamily: "'GT Pressura Mono', monospace" }}
        >
          {opportunities.length}
        </span>
      </div>

      {/* Cards Container */}
      <div className="flex-1 overflow-y-auto p-3 space-y-2 min-h-[200px]">
        <SortableContext
          items={sortedOpportunities.map(o => o.id.toString())}
          strategy={verticalListSortingStrategy}
        >
          {sortedOpportunities.map(opportunity => (
            <OpportunityCard key={opportunity.id} opportunity={opportunity} />
          ))}
        </SortableContext>

        {opportunities.length === 0 && (
          <div className={`text-center py-8 transition-all duration-200 ${
            isValidDropTarget ? 'py-12 border-2 border-dashed border-[#a1c6a2] mx-1 my-1 bg-[rgba(161,198,162,0.1)]' : ''
          }`}>
            <span
              className={`text-[10px] tracking-[0.1em] uppercase ${
                isValidDropTarget ? 'text-[#a1c6a2]' : 'text-[rgba(255,251,247,0.3)]'
              }`}
              style={{ fontFamily: "'GT Pressura Mono', monospace" }}
            >
              {isValidDropTarget ? 'Drop here' : 'No opportunities'}
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

export default StatusColumn;
